/* agmf-delivery-card.js — the delivery-by card, inside the feed.
 *
 * Dorian 2026-09-28: "Lets add a new card: the delivery by (in addition to
 * the the regular one) and it should include an add to card button also /
 * Don't need to be 3:4 size".
 *
 * IN ADDITION TO, NOT INSTEAD OF. The buy column keeps its delivery widget
 * exactly as it is. This is a second place the same answer appears, for a
 * shopper who is deep in the feed and a long way from the buy column.
 *
 * IT MIRRORS, IT DOES NOT RECOMPUTE. delivery-estimate.js already fetches the
 * panel envelope, resolves the shopper's country, counts working days around
 * the cutoff, localises the dates and runs the countdown. Working any of that
 * out a second time would be two answers that can disagree - and they WOULD
 * disagree, because the one thing a second implementation never copies is the
 * next config change. So this clones what that widget has already rendered
 * into the buy column and keeps the clone in step with a MutationObserver.
 * The clock is the reason the observer watches characterData too: the shared
 * one-second interval writes into the ORIGINAL nodes, so the copy re-clones
 * as it ticks rather than freezing at the second it was made.
 *
 * WHICH ALSO MEANS THE CARD DISAPPEARS WHEN IT SHOULD. The widget renders
 * nothing at all for a shopper outside the shipped countries, for a variant
 * that is out of stock, when no free-shipping rule matches, and when the panel
 * has it switched off. An empty source hides this card - the hard cases are
 * answered once, in the file that owns them.
 *
 * THE BUTTON CLICKS THE REAL ONE through agmf-product-bridge.js: the chosen
 * variant, Gear Builder loadout, EasySuggestions, quantity, body-armour gate,
 * cart cap and free-gift row all behave as if the shopper pressed the active
 * page button, because they did. Mirroring that button's LABEL is what makes
 * this card say "Adding to cart" and then "Added" without knowing those words
 * exist (agb-atc-added.js writes them onto the real button).
 *
 * SO THE CARD CARRIES NO COPY OF ITS OWN. Every word in it arrives from a
 * system that is already translated - which is the only way a card in a feed
 * on a 29-language storefront stays honest.
 *
 * NOT 3:4: the feed's 3:4 belongs to the MEDIA shell (.agmf-media--43 and its
 * siblings), not to .agmf-card. A card with no media shell is its own height
 * already, the way the Ask card is.
 *
 * Isolation: own file, own stylesheet. It reads the DOM of two other features
 * and edits neither. Kill: window.AGMF_DELIVERY_CARD_OFF = true.
 */
(function () {
  'use strict';
  if (window.__agmfDeliveryCard || window.AGMF_DELIVERY_CARD_OFF) return;
  window.__agmfDeliveryCard = true;

  var started = false;
  var cards = [];                       // every card placed, so all stay in step

  function product() { return window.AGMF_PRODUCT_BRIDGE || null; }
  function source() { var p = product(); return p && p.getDeliveryElement(); }
  function atcSource() { var p = product(); return p && p.getAddToCartButton(); }

  /* ---- one card ---------------------------------------------------------- */

  function makeCard() {
    var card = document.createElement('article');
    card.className = 'agmf-card agmf-dcard';
    card.setAttribute('data-agmf-delivery-card', '');
    card.hidden = true;                 // until the widget has something to say

    var mirror = document.createElement('div');
    mirror.className = 'agmf-dcard__mirror';
    // The clone is decoration: the real one is a few hundred pixels away in
    // the buy column and is the one a screen reader should read.
    mirror.setAttribute('aria-hidden', 'true');
    card.appendChild(mirror);

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'agmf-dcard__atc';
    btn.setAttribute('data-agmf-delivery-atc', '');
    btn.addEventListener('click', function (e) {
      // the card itself is clickable in the feed; this press is not that
      e.stopPropagation();
      var p = product();
      if (p) p.addToCart();
    });
    card.appendChild(btn);

    cards.push({ card: card, mirror: mirror, btn: btn });
    paintOne(cards[cards.length - 1]);

    // WAS IT ON SCREEN (Dorian 2026-09-29: "add to the audience tracking ...
    // also the delivered by card"). The card carries no content key, so the
    // view tracker never counts it; like the Ask card, it says so itself -
    // once per card, the first time at least half of it is on screen. A card
    // hidden for having nothing to promise never intersects, so it is never
    // counted as shown. agmf-track.js turns this into agmf_delivery_shown.
    try {
      if ('IntersectionObserver' in window) {
        var seenOnce = new IntersectionObserver(function (entries) {
          for (var i = 0; i < entries.length; i++) {
            if (entries[i].isIntersecting && entries[i].intersectionRatio >= 0.5) {
              seenOnce.disconnect();
              var host = card.parentElement;
              var slot = host ? Array.prototype.indexOf.call(host.children, card) : -1;
              try {
                document.dispatchEvent(new CustomEvent('agmf:delivery', { detail: { kind: 'shown', slot: slot } }));
              } catch (e) { /* no CustomEvent: the card works, it just goes uncounted */ }
              return;
            }
          }
        }, { threshold: [0.5] });
        seenOnce.observe(card);
      }
    } catch (e) { /* no observer: the card works, it just goes uncounted */ }
    return card;
  }

  function paintOne(rec) {
    var src = source();
    var html = src ? src.innerHTML : '';
    var has = !!(html && html.trim());
    rec.card.hidden = !has;
    if (has && rec.mirror.innerHTML !== html) rec.mirror.innerHTML = html;
    if (!has && rec.mirror.firstChild) rec.mirror.innerHTML = '';

    var s = atcSource();
    if (s) {
      rec.btn.hidden = false;
      var label = (s.textContent || '').replace(/\s+/g, ' ').trim();
      if (label && rec.btn.textContent !== label) rec.btn.textContent = label;
      var off = !!s.disabled || s.getAttribute('aria-disabled') === 'true';
      rec.btn.disabled = off;
      rec.btn.setAttribute('aria-disabled', String(off));
    } else {
      // Never leave behind a blank or stale control when the buy column has
      // no active button (for example during a responsive shell swap).
      rec.btn.hidden = true;
      rec.btn.textContent = '';
      rec.btn.disabled = true;
      rec.btn.setAttribute('aria-disabled', 'true');
    }
  }

  function paintAll() { for (var i = 0; i < cards.length; i++) paintOne(cards[i]); }

  /* ---- where it sits in the feed ----------------------------------------- */

  function start(section) {
    if (started) return;

    var feed = section.querySelector('[data-agmf-feed]');
    if (!feed) return;
    if (!product()) return;             // bridge is the only product-shell seam
    var runtime = window.AGMF_CARD_RUNTIME;
    if (!runtime) return;
    if (!source()) return;              // no widget on this page: nothing to mirror
    // LATCHED AFTER THE GUARDS, not before: the buy column's widget renders
    // after its own config fetch, so a source that arrives late used to find
    // the card permanently switched off with nothing left to retry.
    started = true;

    /* Same recipe shape the Ask card reads, under its own key, so where this
       card falls is published with the feed rather than pinned in the theme.
       Defaults stand in until the key exists: once, early, which is where a
       delivery promise is worth reading.

       THE RECIPE IS READ LATE, AND THAT IS THE WHOLE POINT.
       `window.AGMF_FEED` is assigned by agilite-media-flow.js only once the
       feed JSON has been fetched and parsed - a network round trip after every
       deferred script on the page has already run. Reading it in start(), as
       this once did, therefore always saw `{}` and the card quietly used the
       baked defaults. Measured on the preview 2026-09-30: Media published
       `every: 3`, the feed grew to 24 cycles, and there was exactly ONE
       delivery card on the page - while the Ask card, whose boot waits,
       placed six. The setting was published, shipped and ignored.
       So `recipe` is a function, and agmf-card-runtime.js calls it only once
       the first cycle exists (or the feed has ended), by which time the feed
       has certainly published. The runtime also keeps a deliberate position 0
       (the very top of the feed is a real choice in Media's dropdown, not the
       absence of one) and honours `enabled: false`. The baked defaults still
       stand in when the key is genuinely absent - an older bake of the feed
       asset has no `deliveryCard` at all. */
    product().subscribe(function () { paintAll(); });
    runtime.mount({
      id: 'delivery-feed',
      section: section,
      feed: feed,
      recipe: function () { return (window.AGMF_FEED && window.AGMF_FEED.deliveryCard) || {}; },
      defaultPosition: 1,
      defaultEvery: 0,
      maxCards: 3,
      create: makeCard,
      onUpdate: paintAll
    });
  }

  /* WAIT UNTIL THE FEED HAS PUBLISHED. `[data-agmf]` is static Liquid
     markup, so the old boot found it during defer execution and started at
     once - before the feed had been fetched, which is why the recipe above was
     always empty. The first `.agmf-cycle` is the signal: a cycle cannot exist
     until the feed JSON has been parsed and built. This is the Ask card's boot
     (agmf-ask-card.js), for the same reason, including its failsafe - a feed
     that never produces a cycle must not cost the card entirely. */
  function boot() {
    var section = document.querySelector('[data-agmf]');
    if (!section) return false;
    if (section.querySelector('.agmf-cycle')) { start(section); return true; }
    var failsafe = setTimeout(function () { obs.disconnect(); start(section); }, 8000);
    var obs = new MutationObserver(function () {
      if (!section.querySelector('.agmf-cycle')) return;
      obs.disconnect();
      clearTimeout(failsafe);
      start(section);
    });
    obs.observe(section, { childList: true, subtree: true });
    return true;
  }

  if (!boot()) {
    var tries = 0;
    var iv = setInterval(function () {
      if (boot() || ++tries > 40) clearInterval(iv);
    }, 250);
  }
})();
